import { AnyAction } from 'redux';
import {
  STREAM__WITH_CURSOR__INITIALIZE,
  STREAM__WITH_CURSOR__SET_CURSOR,
  STREAM__WITH_CURSOR__FETCH,
  STREAM__WITH_CURSOR__FETCH_SUCCESS,
  STREAM__WITH_CURSOR__FETCH_SUCCESS_DEDUPE,
  STREAM__WITH_CURSOR__INSERT_ITEMS,
  setCursor,
  fetchPrevious,
  fetchPreviousSuccess,
  emptyStream,
  resetStream,
  fetchAllSuccess,
} from './actions';

import {
  InitializeAction,
  FetchSuccessAction,
  FetchSuccessDedupeAction,
  InsertItemsAtOffsetAndSetCursorAction,
  CursorStream,
  CursorStreamActionHandlers,
} from './types';

import {
  handleInitAction,
  handleFetchAction,
  handleFetchSuccessAction,
  handleFetchPreviousSuccessAction,
  commonActionHandlers,
  handleEmptyStream,
  handleResetStream,
  handleFetchAllSuccessAction,
} from '../base/actionHandlers';
import { handleInsertItemsAtOffsetAndSetCursorAction } from './actionHandlers';
import { StreamType } from '../types';
import { defaultStreamState } from '../constants';

const actionHandlers: CursorStreamActionHandlers = {
  [STREAM__WITH_CURSOR__INITIALIZE]: (streamState, action: InitializeAction) => {
    return {
      ...handleInitAction(streamState, action),
      cursor: action.cursor,
      prevCursor: action.prevCursor,
    };
  },
  [STREAM__WITH_CURSOR__SET_CURSOR]: (streamState, action: ReturnType<typeof setCursor>) => ({
    ...streamState,
    cursor: action.cursor,
  }),
  [STREAM__WITH_CURSOR__FETCH]: streamState => handleFetchAction(streamState),
  [STREAM__WITH_CURSOR__FETCH_SUCCESS]: (streamState, action: FetchSuccessAction) => {
    if (action.prevCursor) {
      return {
        ...handleFetchSuccessAction(streamState, action),
        cursor: action.cursor,
        prevCursor: action.prevCursor,
      };
    }
    return {
      ...handleFetchSuccessAction(streamState, action),
      cursor: action.cursor,
    };
  },
  [STREAM__WITH_CURSOR__FETCH_SUCCESS_DEDUPE]: (streamState, action: FetchSuccessDedupeAction) => {
    const { items = [] } = streamState;
    const {
      items: actionItems,
      dedupeFunction = (item, existingItems) => !existingItems.includes(item),
      cursor,
      ...baseAction
    } = action;

    // we want to dedupe already existing items from the stream
    const filteredItems = actionItems?.filter(item => dedupeFunction(item, items));

    return {
      ...handleFetchSuccessAction(streamState, {
        ...baseAction,
        items: filteredItems,
      }),
      cursor,
      prevCursor: action.prevCursor ?? streamState.prevCursor,
    };
  },
  [STREAM__WITH_CURSOR__INSERT_ITEMS]: (
    streamState,
    action: InsertItemsAtOffsetAndSetCursorAction
  ) => handleInsertItemsAtOffsetAndSetCursorAction(streamState, action),

  [fetchPrevious.toString()]: handleFetchAction,
  [fetchPreviousSuccess.toString()]: (
    streamState,
    action: ReturnType<typeof fetchPreviousSuccess>
  ) => ({
    ...handleFetchPreviousSuccessAction(streamState, action),
    prevCursor: action.payload.prevCursor,
  }),
  [fetchAllSuccess.toString()]: (streamState, action: ReturnType<typeof fetchAllSuccess>) => ({
    ...handleFetchAllSuccessAction(streamState, action),
    prevCursor: undefined,
    cursor: undefined,
  }),
  [emptyStream.toString()]: (streamState, action: ReturnType<typeof emptyStream>) => ({
    ...handleEmptyStream(streamState, action),
    cursor: undefined,
    prevCursor: undefined,
  }),
  [resetStream.toString()]: (streamState, action: ReturnType<typeof resetStream>) => ({
    ...handleResetStream(streamState, action),
    cursor: undefined,
    prevCursor: undefined,
  }),
  ...(commonActionHandlers as CursorStreamActionHandlers),
};

const reducer = (
  streamState: CursorStream = {
    ...defaultStreamState,
    streamType: StreamType.WITH_CURSOR,
  },
  action: AnyAction
) => {
  const handler = actionHandlers[action.type];

  return (handler && handler(streamState, action)) || streamState;
};

export default reducer;
